/**
 * use-business-type — Fleet label helper driven by Organization.businessType.
 *
 * Pure computation from the `businessType` field on the company-info endpoint
 * (`useCompanyInfo`). The returned `FleetLabels` object is consumed by:
 *
 *   - the top-nav Fleet group label (Sidebar)
 *   - the FleetGridPage "New" button text
 *   - the FleetPartyFormModal header title (create mode)
 *   - the FleetPartyFormModal footer save button (create mode)
 *
 * Label rule:
 *
 *   | businessType | Nav            | Button                    | Modal header       | Save button      |
 *   |--------------|----------------|---------------------------|--------------------|------------------|
 *   | Broker       | Carriers       | New Carrier               | New Carrier        | Save Carrier     |
 *   | Mixed        | Drivers & Carriers | New Driver / Carrier  | New {type}         | Save {type}      |
 *   | Carrier      | Drivers        | New Driver                | New Driver         | Save Driver      |
 *   | Shipper      | Drivers        | New Driver                | New Driver         | Save Driver      |
 *   | Other / null | Drivers        | New Driver                | New Driver         | Save Driver      |
 *
 * While `useCompanyInfo()` is loading or errored, the hook falls back to the
 * "Drivers" variant — the safest default because it matches the legacy behavior
 * (a single "New Driver" button + a "Drivers" label) that every existing test
 * fixture assumes. The Mixed variant is the only branch that asks the user to
 * pick the party type inside the modal.
 *
 * Edit mode is NOT affected by businessType — the modal header stays "Edit
 * Driver" / "Edit Carrier" based on the row's own type. Consumers derive that
 * from `initialData._type` (or the equivalent) and only call
 * `labels.modalCreateHeader` in create mode.
 */

import { useMemo } from 'react';
import { useCompanyInfo } from './use-settings';

export interface FleetLabels {
  /** Sidebar nav label for the Fleet entry. */
  navLabel: string;
  /** FleetGridPage page-shell header. Broker → "Carrier", Mixed → "Driver / Carrier", else → "Driver". */
  pageHeader: string;
  /** FleetGridPage "New" action button label. */
  newButton: string;
  /**
   * Modal create-mode header. Accepts the picker's current selection for the
   * Mixed branch; ignored for the non-Mixed branches. In Mixed we default to
   * "New Driver" if nothing is passed so the header renders something sane
   * before a user makes a pick.
   */
  modalCreateHeader: (type?: 'driver' | 'carrier') => string;
  /** Footer save-button label in create mode. */
  modalSaveButton: (type: 'driver' | 'carrier') => string;
  /**
   * Initial party type for a new record. `undefined` signals Mixed — the
   * caller must render the in-modal type picker before a DTO is chosen.
   */
  defaultPartyType: 'driver' | 'carrier' | undefined;
  /** True only for Mixed. The modal uses this to show/hide the Type picker. */
  showTypePickerInModal: boolean;
}

/**
 * Pure label resolver — exported for tests that want to drive the mapping
 * without setting up react-query. Consumers inside the app should prefer
 * `useFleetLabels()`, which wires up the live `useCompanyInfo()` response.
 */
export function resolveFleetLabels(
  businessType: string | null | undefined,
): FleetLabels {
  const bt = businessType ?? '';

  if (bt === 'Broker') {
    return {
      navLabel: 'Carriers',
      pageHeader: 'Carrier',
      newButton: 'New Carrier',
      modalCreateHeader: () => 'New Carrier',
      modalSaveButton: () => 'Save Carrier',
      defaultPartyType: 'carrier',
      showTypePickerInModal: false,
    };
  }

  if (bt === 'Mixed') {
    return {
      navLabel: 'Drivers & Carriers',
      pageHeader: 'Driver / Carrier',
      newButton: 'New Driver / Carrier',
      // Header + save button mirror the combined phrasing regardless of the
      // in-modal type picker — the user-visible label stays consistent with
      // the page header and the button.
      modalCreateHeader: () => 'New Driver / Carrier',
      modalSaveButton: () => 'Save Driver / Carrier',
      // Mixed mode: no default — caller renders the in-modal picker so the
      // user picks Driver or Carrier before typing anything.
      defaultPartyType: undefined,
      showTypePickerInModal: true,
    };
  }

  // Carrier, Shipper, Other, null, loading, error — all collapse to the
  // drivers-only label set. This matches the legacy behavior that existed
  // before WAYP-506's businessType wiring, so every test fixture + brownfield
  // install keeps working with no mock in place.
  return {
    navLabel: 'Drivers',
    pageHeader: 'Driver',
    newButton: 'New Driver',
    modalCreateHeader: () => 'New Driver',
    modalSaveButton: () => 'Save Driver',
    defaultPartyType: 'driver',
    showTypePickerInModal: false,
  };
}

/**
 * React hook. Wraps `useCompanyInfo()` and returns a memoized `FleetLabels`.
 *
 * - While loading or on error, the returned labels fall back to the "Drivers"
 *   variant — callers do not need to branch on loading state themselves.
 * - `data?.businessType` is the only input; other company-info fields are
 *   ignored here so the memo is cheap.
 */
export function useFleetLabels(): FleetLabels {
  const { data } = useCompanyInfo();
  const businessType = data?.businessType ?? null;
  return useMemo(() => resolveFleetLabels(businessType), [businessType]);
}
